import {
  objectFitCoverImageStyles,
  SDUIImage,
} from "@faire/deprecated-retailer-visitor-shared/components/SDUI/__internal__/Image/SDUIImage";
import { convertServerResponsivePropertyToClientResponsiveProperty } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/__internal__/Styling/responsiveValues";
import {
  ComponentLoggingEventParams,
  SDUIComponentInstanceRendererProps,
} from "@faire/deprecated-retailer-visitor-shared/components/SDUI/__internal__/types";
import { SDUIButtonRenderer } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/ComponentRenderers/Button/SDUIButtonRenderer";
import {
  brandHeaderDesktopSubTitle,
  brandHeaderMobileSubTitle,
  LOGGING_DATA_FIELD_SEPARATOR,
} from "@faire/deprecated-retailer-visitor-shared/components/SDUI/ComponentRenderers/Header/__internal__/BrandSearchHeaderUtils";
import { SDUITypographyRenderer } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/ComponentRenderers/Typography/SDUITypographyRenderer";
// eslint-disable-next-line @faire/enforce-internal-folder-encapsulation
import { BrandNameArrow } from "@faire/deprecated-retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/__internal__/SearchPageHeaderAndResults/__internal__/SearchPageHeader/__internal__/SearchPageBrandSearch/BrandSearch/__internal__/BrandNameArrow";
import { IAction } from "@faire/web-api--source/indigofair/data/IAction";
import { IButtonElement } from "@faire/web-api--source/indigofair/data/IButtonElement";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import { ILoggingAction } from "@faire/web-api--source/indigofair/data/ILoggingAction";
import { INavigateAction } from "@faire/web-api--source/indigofair/data/INavigateAction";
import { ITextElement } from "@faire/web-api--source/indigofair/data/ITextElement";
import { Column, Flex, Row } from "@faire/web/slate/Layout";
import React from "react";

import { SDUIOnClickHandler } from "../../__internal__/SDUIOnClickHandler/SDUIOnClickHandler";
import { SDUIAvatarElementRenderer } from "../Avatar/SDUIAvatarElementRenderer";

export const BRAND_SEARCH_MOBILE_TITLE_INSTANCE_IDENTIFIER =
  "brand_search_mobile_title";
export const BRAND_SEARCH_DESKTOP_TITLE_INSTANCE_IDENTIFIER =
  "brand_search_desktop_title";
export const BRAND_SEARCH_MOBILE_SUB_TITLE_INSTANCE_IDENTIFIER =
  "brand_search_mobile_sub_title";
export const BRAND_SEARCH_DESKTOP_SUB_TITLE_INSTANCE_IDENTIFIER =
  "brand_search_desktop_sub_title";
export const BRAND_SEARCH_DESKTOP_SHOP_BRAND_BUTTON_INSTANCE_IDENTIFIER =
  "brand_search_desktop_shop_brand_button";
export const shopAllEventName = "search-product_carousel_brand-click";
export const brandNameEventContainer = "brand_search_carousel|brandName";

const HEADER_HEIGHT = 300;

export const SDUIHeaderRenderer: React.FC<
  SDUIComponentInstanceRendererProps
> = ({ component, loggingData, renderBehaviour }) => {
  const headerComponent = component.header_component;
  if (!headerComponent) {
    return null;
  }
  const {
    tagline,
    title,
    sub_title,
    inline_cta_button,
    button_list,
    responsive_background_image,
    avatar_element,
    on_click,
  } = headerComponent;

  const clientResponsiveImage =
    convertServerResponsivePropertyToClientResponsiveProperty(
      IImage.build(),
      (img) => img,
      responsive_background_image
    );

  return (
    <Flex
      direction={{
        mobileAndAbove: "column",
        tabletAndAbove: "row",
      }}
      align={{
        mobileAndAbove: "start",
        tabletAndAbove: "center",
      }}
      className="relative z-4 w-full justify-between gap-y-4"
    >
      <Flex
        direction="row"
        className={`relative z-4 justify-between gap-y-4 ${button_list.length === 0 ? "w-full" : ""}`}
      >
        <SDUIImage
          responsiveImage={clientResponsiveImage}
          optimizeImageOptions={{ height: HEADER_HEIGHT }}
          showPlaceholder={false}
          renderBehaviour={renderBehaviour}
          className="absolute top-0 left-0 z-0 h-full w-full"
          imageStyles={objectFitCoverImageStyles}
        />
        {avatar_element ? (
          <SDUIAvatarElementRenderer
            avatarElement={avatar_element}
            loggingData={loggingData}
            className="z-2 pr-[8px] lg:pr-[16px]"
          />
        ) : null}
        <Column
          gap="1x"
          className="z-1 w-full shrink grow-0 basis-full lg:w-auto"
        >
          {tagline ? (
            <SDUITypographyRenderer
              textElement={tagline}
              loggingData={loggingData}
            />
          ) : null}
          <Title
            title={title}
            loggingData={loggingData}
            navigateAction={
              on_click?.[0]?.navigate ?? avatar_element?.on_click?.[0]?.navigate
            }
          />
          <SubTitle
            subTitle={sub_title}
            loggingData={loggingData}
            inlineCtaButton={inline_cta_button}
          />
        </Column>
      </Flex>
      <DesktopButtons buttonList={button_list} loggingData={loggingData} />
    </Flex>
  );
};

const Title = ({
  title,
  loggingData,
  navigateAction,
}: {
  title: ITextElement | undefined;
  loggingData: ComponentLoggingEventParams;
  navigateAction: INavigateAction | undefined;
}) => {
  if (!title) {
    return null;
  }
  // This is a disgusting hack to get the arrow next to the brand name to render on mobile.
  // The logging string isn't used for logging, it is used to identify when we are rendering a mobile brand name
  // brand search header and hardcode the arrow "button" beside it.
  // We then want the arrow to be a link but don't know where we want it to go, so we pass in the link pulled
  // from the brand avatar (that we know exists because brand search on mobile always has an avater) and use
  // it here.
  // I don't like it, you don't like it, I don't like myself, you don't like me, but here we are.
  // https://fairewholesale.atlassian.net/browse/FD-268612
  if (
    (title.logging_data?.instance_identifier?.startsWith(
      BRAND_SEARCH_MOBILE_TITLE_INSTANCE_IDENTIFIER
    ) ||
      title.logging_data?.instance_identifier?.startsWith(
        BRAND_SEARCH_DESKTOP_TITLE_INSTANCE_IDENTIFIER
      )) &&
    navigateAction?.link
  ) {
    const brandToken =
      title.logging_data?.instance_identifier?.split(
        LOGGING_DATA_FIELD_SEPARATOR
      )[1] ?? "";
    const requestId =
      title.logging_data?.instance_identifier?.split(
        LOGGING_DATA_FIELD_SEPARATOR
      )[2] ?? "";
    return (
      <Row align="center">
        <SDUIOnClickHandler
          onClickActions={[
            IAction.build({
              navigate: navigateAction,
            }),
            IAction.build({
              log_event: ILoggingAction.build({
                event_name: shopAllEventName,
                parameters: [brandNameEventContainer, requestId, brandToken],
              }),
            }),
          ]}
          loggingData={loggingData}
        >
          <SDUITypographyRenderer
            textElement={title}
            loggingData={loggingData}
          />
        </SDUIOnClickHandler>
        {title.logging_data?.instance_identifier?.startsWith(
          BRAND_SEARCH_MOBILE_TITLE_INSTANCE_IDENTIFIER
        ) ? (
          <BrandNameArrow linkTo={navigateAction.link} />
        ) : null}
      </Row>
    );
  }
  return (
    <SDUITypographyRenderer
      textElement={title}
      loggingData={loggingData}
      linkTo={navigateAction?.link}
    />
  );
};

const SubTitle = ({
  subTitle,
  loggingData,
  inlineCtaButton,
}: {
  subTitle: ITextElement | undefined;
  loggingData: ComponentLoggingEventParams;
  inlineCtaButton: IButtonElement | undefined;
}) => {
  if (!subTitle) {
    return null;
  }
  const brandSearchHeaderLoggingData =
    subTitle.logging_data?.instance_identifier?.split(
      LOGGING_DATA_FIELD_SEPARATOR
    );
  // This is a disgusting hack to get the arrow next to the brand name to render on mobile.
  // The logging string isn't used for logging, it is used to identify when we are rendering a brand subtitle
  // for the brand search header and hardcode the icons inside it.
  // https://fairewholesale.atlassian.net/browse/FD-268612
  if (
    brandSearchHeaderLoggingData?.[0] ===
    BRAND_SEARCH_MOBILE_SUB_TITLE_INSTANCE_IDENTIFIER
  ) {
    return brandHeaderMobileSubTitle(brandSearchHeaderLoggingData.slice(1));
  }
  if (
    brandSearchHeaderLoggingData?.[0] ===
    BRAND_SEARCH_DESKTOP_SUB_TITLE_INSTANCE_IDENTIFIER
  ) {
    return brandHeaderDesktopSubTitle(brandSearchHeaderLoggingData.slice(1));
  }

  return (
    <Row align="start" gap="1/2x">
      {inlineCtaButton ? (
        <SDUIButtonRenderer
          buttonElement={inlineCtaButton}
          loggingData={loggingData}
          className="!gap-x-1 !no-underline"
        />
      ) : null}
      <SDUITypographyRenderer
        textElement={subTitle}
        loggingData={loggingData}
      />
    </Row>
  );
};

const DesktopButtons = ({
  buttonList,
  loggingData,
}: {
  buttonList: IButtonElement[];
  loggingData: ComponentLoggingEventParams;
}) => {
  if (buttonList.length === 0) {
    return null;
  }

  return (
    <Row
      gap="1x"
      className="w-full shrink-0 grow basis-auto md:w-auto md:shrink md:grow-0"
    >
      {buttonList.map((buttonElement, i) => (
        <SDUIButtonRenderer
          key={`${i}-${buttonElement.logging_data?.instance_identifier}`}
          loggingData={loggingData}
          buttonElement={buttonElement}
          minWidth={
            buttonElement.logging_data?.instance_identifier ===
            BRAND_SEARCH_DESKTOP_SHOP_BRAND_BUTTON_INSTANCE_IDENTIFIER
              ? "160px"
              : undefined
          }
          width=""
          className="w-full md:w-auto"
        />
      ))}
    </Row>
  );
};
